import moment from "moment"
import { IEvent } from "../../components/Events/types"

// Semantic buckets that drive both the section grouping and the top-level
// tabs. Chosen for what users actually think about:
//   upcoming→ "I need to prepare" — the everyday workspace
//   draft   → "unfinished work" — user has something to finish
//   past    → "archive" — rarely actionable, collapsed
//
// We used to carve out a separate `live` bucket for "happening today",
// but product decided today's events belong with Upcoming — today is
// just the most imminent upcoming event, and splitting it into its own
// tab added ceremony without much payoff.
export type EventBucket = "upcoming" | "draft" | "past"

export type TabId = "all" | "upcoming" | "drafts" | "past"

interface Classified {
  event: IEvent
  bucket: EventBucket
  startMoment: moment.Moment | null
}

// A draft is an event the user hasn't finished setting up. We treat it as a
// distinct state regardless of its start_datetime — a draft scheduled for
// next year still needs finishing before the date matters.
const isDraft = (event: IEvent): boolean => event.status === 0

export const classify = (event: IEvent): Classified => {
  const startRaw = event.start_datetime
  const startMoment = startRaw ? moment(startRaw) : null

  if (isDraft(event)) {
    return { event, bucket: "draft", startMoment }
  }

  // Strictly before today → past. Today itself falls through to upcoming
  // so operators still see it in their default view until it ends.
  if (startMoment && startMoment.isBefore(moment(), "day")) {
    return { event, bucket: "past", startMoment }
  }

  // Default to upcoming — covers today, future-dated events, and events
  // with no start_datetime yet (treat them as "pending, needs scheduling"
  // rather than hiding them in past).
  return { event, bucket: "upcoming", startMoment }
}

export interface GroupedEvents {
  upcoming: Classified[]
  draft: Classified[]
  past: Classified[]
}

export const groupEvents = (events: IEvent[]): GroupedEvents => {
  const groups: GroupedEvents = {
    upcoming: [],
    draft: [],
    past: [],
  }

  // Preserve the incoming array's order as each bucket is populated.
  //
  // The caller is responsible for handing us events in the user's chosen
  // order — in practice that comes from the API (which already applied
  // `orderBy=<sortField>&sortedBy=<sortDir>` per the toolbar selection).
  // If we resorted here, every tab except "All" would silently override
  // the user's sort choice, which is exactly the bug we had before.
  events.forEach((event) => {
    const classified = classify(event)
    groups[classified.bucket].push(classified)
  })

  return groups
}

// Given a tab, return only the events that belong to it. Used to compute
// tab counts and to filter the displayed list when a specific tab is active.
export const filterByTab = (
  events: IEvent[],
  tab: TabId,
): IEvent[] => {
  if (tab === "all") return events
  const groups = groupEvents(events)
  const bucketForTab: Record<Exclude<TabId, "all">, EventBucket> = {
    upcoming: "upcoming",
    drafts: "draft",
    past: "past",
  }
  return groups[bucketForTab[tab]].map((c) => c.event)
}

// Counts per tab, used to render small badge counters next to tab labels.
export const countByTab = (events: IEvent[]): Record<TabId, number> => {
  const groups = groupEvents(events)
  return {
    all: events.length,
    upcoming: groups.upcoming.length,
    drafts: groups.draft.length,
    past: groups.past.length,
  }
}
